#!/usr/bin/env node
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { Token, TokenAmount } from "@saberhq/token-utils";
import type { GetProgramAccountsConfig } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import BN from "bn.js";
import base58 from "bs58";
import { Command } from "commander";
import { printTable } from "console-table-printer";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import type { MergeMinerData, MergePoolData } from "../src";
import { QUARRY_ADDRESSES, QUARRY_CODERS } from "../src";
import { getMiners } from "./mine";
import {
  catchFinallyExit,
  confirmTx,
  makeSDK,
  printObjectJSON,
  printObjectTable,
  receiptLog,
} from "./utils";

export const program = new Command().name("crema-merge-mine");
program.version(pack.version);

const poolCmd = program
  .command("pool")
  .description("The quary_merge_mine:MergePool sub commands");

poolCmd
  .command("new")
  .description("Create a quary_merge_mine:MergePool")
  .requiredOption("-m, --mint <mint>", "The primary token mint address")
  .action((arg) => {
    const mint = new PublicKey(arg.mint);
    catchFinallyExit(newPool(mint));
  });

poolCmd
  .command("info")
  .description("Fetch the quary_merge_mine:MergePool information")
  .requiredOption("-m, --mint <mint>", "The primary token mint address")
  .action((arg) => {
    const mint = new PublicKey(arg.mint);
    catchFinallyExit(fetchMergePool(mint));
  });

poolCmd
  .command("list")
  .description("Fetch the quarry_merge_mine:MergePool list")
  .action(() => {
    catchFinallyExit(fetchMergePools());
  });

const mmCmd = program
  .command("mm")
  .description("The quary_merge_mine:MergeMiner sub commands");

mmCmd
  .command("new")
  .description(
    "Create a merge miner and init a quarry_mine:Miner for primary token"
  )
  .requiredOption("-p, --pool <pool>", "The merge pool address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(newMergeMiner(mpKey, rewarderKey));
  });

mmCmd
  .command("init-miner")
  .description("Init a [quarry_mine:Miner] owned by the [MergeMiner]")
  .requiredOption("-m, --mint <mint>", "The stake mint address")
  .requiredOption("-p, --pool <pool>", "The merge pool address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mint = new PublicKey(arg.mint);
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(initMiner(mint, mpKey, rewarderKey));
  });

mmCmd
  .command("stake-replica")
  .description("Stake the replica tokens into a miner.")
  .requiredOption("-p, --pool <pool>", "The merge pool address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const rewarderKey = new PublicKey(arg.rewarder);
    const mpKey = new PublicKey(arg.pool);
    catchFinallyExit(stakeReplica(mpKey, rewarderKey));
  });

mmCmd
  .command("deposit")
  .description("Deposit the primary tokens to the merge miner")
  .requiredOption("-p, --pool <pool>", "The merge pool address")
  .requiredOption("-a, --amount <amount>", "The deposit amount")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const amount = new BN(arg.amount);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(deposit(mpKey, rewarderKey, amount));
  });

mmCmd
  .command("withdraw")
  .description("Withdraw staked tokens from a merge miner.")
  .requiredOption("-p, --pool <pool>", "The merge miner address")
  .requiredOption("-a, --amount <amount>", "The token amount")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    const amount = new BN(arg.amount);
    catchFinallyExit(withdraw(mpKey, rewarderKey, amount));
  });

mmCmd
  .command("unstake-replica")
  .description("Unstakes all replica tokens from a quarry.")
  .requiredOption("-p, --pool <pool>", "The merge miner address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(unstakeReplica(mpKey, rewarderKey));
  });

mmCmd
  .command("claim-primary")
  .description("Claims rewards for a primary account.")
  .requiredOption("-p, --pool <pool>", "The merge miner address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(claimPrimary(mpKey, rewarderKey));
  });

mmCmd
  .command("claim-replica")
  .description("Claims rewards for a replica account.")
  .requiredOption("-p, --pool <pool>", "The merge miner address")
  .requiredOption("-r, --rewarder <rewarder>", "The quarry rewarder address")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const rewarderKey = new PublicKey(arg.rewarder);
    catchFinallyExit(claimReplica(mpKey, rewarderKey));
  });

mmCmd
  .command("info")
  .description("Fetch the merge miner information")
  .requiredOption("-p, --pool <pool>", "The merge pool address")
  .option("-o, --owner <owner>", "The merge miner owner")
  .action((arg) => {
    const mpKey = new PublicKey(arg.pool);
    const owner =
      arg.owner !== undefined ? new PublicKey(arg.owner) : arg.owner;
    catchFinallyExit(fetchMergeMiner(mpKey, owner));
  });

mmCmd
  .command("list")
  .description("Fetch the merge miner list")
  .option("-o, --owner <owner>", "The merge miner owner")
  .action((arg) => {
    const owner: PublicKey | undefined =
      arg.owner === undefined ? undefined : new PublicKey(arg.owner);
    catchFinallyExit(fetchMergeMiners(owner));
  });

async function newPool(mint: PublicKey): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const res = await sdk.mergeMine.newPool({
    primaryMint: mint,
  });
  const receipt = await res.tx.confirm();

  printLog(receipt);

  return receipt;
}

async function fetchMergePool(mint: PublicKey) {
  const sdk = makeSDK();
  const mergePoolKey = await sdk.mergeMine.findPoolAddress({
    primaryMint: mint,
  });
  const data = await sdk.mergeMine.fetchMergePoolData(mergePoolKey);
  printObjectTable({
    mergePoolKey: mergePoolKey.toBase58(),
    primaryMint: mint.toBase58(),
    replicaMint: data.data.replicaMint.toBase58(),
    mmCount: data.data.mmCount.toNumber(),
    totalPrimaryBalance: data.data.totalPrimaryBalance,
    totalReplicaBalance: data.data.totalReplicaBalance,
  });
}

async function fetchMergePools() {
  const sdk = makeSDK();
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.MergeMine,
    {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: base58.encode(
              QUARRY_CODERS.MergeMine.accounts.mergePool.discriminator
            ),
          },
        },
      ],
    }
  );

  const list: any[] = [];
  accounts.forEach((accountInfo) => {
    const pool = QUARRY_CODERS.MergeMine.coder.accounts.decode<MergePoolData>(
      "MergePool",
      accountInfo.account.data
    );
    list.push({
      mergePoolKey: accountInfo.pubkey.toBase58(),
      primaryMint: pool.primaryMint.toBase58(),
      replicaMint: pool.replicaMint.toBase58(),
      mmCount: pool.mmCount.toNumber(),
      totalPrimaryBalance: pool.totalPrimaryBalance,
      totalReplicaBalance: pool.totalReplicaBalance,
    });
  });

  printTable(list);
}

async function newMergeMiner(
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const poolData = await sdk.mergeMine.loadMP({ mpKey: mpKey }).data();
  const res = await sdk.mergeMine.newMM({
    pool: {
      key: mpKey,
      data: poolData,
    },
    rewarder: rewarder.rewarderKey,
    rewardsMint: rewarder.rewarderData.rewardsTokenMint,
  });
  invariant(res.tx !== null, "The tx is null");
  const receipt = await confirmTx(res.tx);
  printLog(receipt);
  return receipt;
}

async function initMiner(
  mint: PublicKey,
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    pool: mpKey,
  });
  const res = await sdk.mergeMine.initMiner({
    mint,
    pool: mpKey,
    mm: mmKey,
    rewarder: rewarderKey,
  });
  const receipt = await confirmTx(res.tx);
  printLog(receipt);
  return receipt;
}

async function deposit(
  mpKey: PublicKey,
  rewarderKey: PublicKey,
  amount: BN
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mp = sdk.mergeMine.loadMP({ mpKey });
  const mpData = await mp.data();
  const primaryToken = await Token.load(
    sdk.provider.connection,
    mpData.primaryMint
  );
  invariant(primaryToken !== null, "The primary token is null");
  const tx = await mp.deposit({
    amount: new TokenAmount(primaryToken, amount),
    rewarder: rewarderKey,
  });

  const receipt = await confirmTx(tx);
  printLog(receipt);
  return receipt;
}

async function stakeReplica(
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mp = sdk.mergeMine.loadMP({ mpKey });
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    owner: sdk.provider.wallet.publicKey,
    pool: mpKey,
  });
  const tx = await mp.stakeReplicaMiner(rewarderKey, mmKey);
  const receipt = await confirmTx(tx);

  printLog(receipt);
  return receipt;
}

async function withdraw(
  mpKey: PublicKey,
  rewarderKey: PublicKey,
  amount: BN
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    pool: mpKey,
    owner: sdk.provider.wallet.publicKey,
  });
  const mm = await sdk.mergeMine.loadMM({ mmKey });
  const primaryToken = await Token.load(
    sdk.provider.connection,
    mm.primaryMint
  );
  invariant(primaryToken !== null, "The primary token is null");
  const tx = await mm.withdraw({
    amount: new TokenAmount(primaryToken, amount),
    rewarder: rewarderKey,
  });

  const receipt = await confirmTx(tx);
  printLog(receipt);
  return receipt;
}

async function unstakeReplica(
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    pool: mpKey,
    owner: sdk.provider.wallet.publicKey,
  });
  const mm = await sdk.mergeMine.loadMM({ mmKey });
  const tx = await mm.unstakeAllReplica(rewarderKey);
  const receipt = await confirmTx(tx);

  printLog(receipt);
  return receipt;
}

async function claimPrimary(
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    pool: mpKey,
    owner: sdk.provider.wallet.publicKey,
  });
  const mm = await sdk.mergeMine.loadMM({ mmKey });
  const tx = await mm.claimPrimaryRewards(rewarderKey);
  const receipt = await confirmTx(tx);
  printLog(receipt);
  return receipt;
}

async function claimReplica(
  mpKey: PublicKey,
  rewarderKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    pool: mpKey,
    owner: sdk.provider.wallet.publicKey,
  });
  const mm = await sdk.mergeMine.loadMM({ mmKey });
  const tx = await mm.claimReplicaRewards(rewarderKey);
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function fetchMergeMiner(mpKey: PublicKey, owner: PublicKey | undefined) {
  const sdk = makeSDK();
  if (owner === undefined) {
    owner = sdk.provider.wallet.publicKey;
  }
  const mmKey = await sdk.mergeMine.findMergeMinerAddress({
    owner,
    pool: mpKey,
  });
  const mm = await sdk.mergeMine.loadMM({ mmKey });

  const miners = await getMiners(sdk, mmKey);

  printObjectJSON(
    {
      mergePool: mm.mm.data.pool.toBase58(),
      owner: mm.mm.data.owner.toBase58(),
      primaryBalance: mm.mm.data.primaryBalance.toString(),
      replicaBalance: mm.mm.data.replicaBalance.toString(),
      miners,
    },
    2
  );
}

async function fetchMergeMiners(owner: PublicKey | undefined) {
  const sdk = makeSDK();
  const config: GetProgramAccountsConfig = {
    filters: [
      {
        memcmp: {
          offset: 0,
          bytes: base58.encode(
            QUARRY_CODERS.MergeMine.accounts.mergeMiner.discriminator
          ),
        },
      },
    ],
  };
  if (owner !== undefined) {
    config.filters?.push({
      memcmp: {
        offset: 40,
        bytes: owner.toBase58(),
      },
    });
  }
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.MergeMine,
    config
  );

  const list: any[] = [];
  accounts.forEach((accountInfo) => {
    const mergeMiner =
      QUARRY_CODERS.MergeMine.coder.accounts.decode<MergeMinerData>(
        "MergeMiner",
        accountInfo.account.data
      );
    list.push({
      mergeMinerKey: accountInfo.pubkey.toBase58(),
      mergePoolKey: mergeMiner.pool.toBase58(),
      owner: mergeMiner.owner.toBase58(),
      primaryBalance: mergeMiner.primaryBalance.toString(),
      replicaBalance: mergeMiner.replicaBalance.toString(),
    });
  });
  printTable(list);
}

function printLog(receipt: TransactionReceipt) {
  const events: Map<string, any> = new Map();
  QUARRY_CODERS.MergeMine.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  ).forEach((event) => {
    events.set(event.name, event.data);
  });
  const e = Object.fromEntries(events);

  printObjectJSON(
    {
      ...receiptLog(receipt),
      events: e,
    },
    2
  );
}
